import { useRef, useState } from 'react';
import { Button } from '@/ui/Button';
import { Badge } from '@/ui/Badge';
import { Card, CardHeader, CardBody } from '@/ui/Card';
import { ConfirmDialog } from '@/ui/Dialog';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoVacio } from '@/components/EstadoVacio';
import { EstadoError } from '@/components/EstadoError';
import { cn } from '@/lib/cn';
import { CampoEnLinea } from './CampoEnLinea';
import { CambiarContrasena } from './CambiarContrasena';
import { MiFicha } from './MiFicha';
import {
  antiguedadDesde, fechaLarga, gestionDe, camposQueMantieneElColaborador, resumenFormacion,
  useCertificados, useConsentimientos, useGuardarPerfil, useKudos, usePerfil, useSubirFoto,
  useCambiarConsentimiento, useMiFicha,
  type Certificado, type Consentimiento, type CursoDelPerfil, type Gestion, type MiFicha as Ficha,
} from './api';

/**
 * Mi perfil — el espacio del colaborador dentro de la organización.
 *
 * ## Lo que esta pantalla tiene que dejar claro
 *
 * No todos sus datos se comportan igual, y confundirlos es el peor defecto que
 * puede tener esta pantalla: si parece que puede corregir su cargo y no puede,
 * lo intenta y se estrella; si parece que su teléfono lo administra Bienestar y
 * en realidad es suyo, no lo actualiza nunca y el portal se queda sin él.
 *
 * **Quién administra cada dato lo dice el SERVIDOR, no este archivo.**
 * `/auth/mi-ficha` devuelve cada columna con `editable`, `sensible` y, cuando no
 * es editable, el `motivo`. Detrás está `campo_editable_por_colaborador()`
 * (employee_db_service.py), que es la única respuesta a esa pregunta en todo el
 * sistema — la misma que usa el endpoint para decidir qué acepta. Aquí no hay
 * ninguna lista de campos escrita a mano: si Bienestar abre o cierra una
 * columna a la autogestión, esta pantalla se entera sola.
 *
 * Las cuatro situaciones, tal como llegan:
 *
 *   editable: true                  → «Mis datos». Lo mantiene el colaborador.
 *   «Lo administra Gestión Humana»  → «Información laboral». Oficial, se pide
 *                                     su corrección a Bienestar.
 *   «Lo calcula el sistema»         → derivado. No se teclea, ni se pide.
 *   «Requiere tu autorización…»     → lo desbloquea el propio colaborador
 *                                     autorizando en Mis consentimientos.
 *
 * Aparte del maestro hay un segundo camino de escritura, y son cosas distintas:
 * `PUT /users/me/profile` acepta EXACTAMENTE tres campos —`bio`, `skills`,
 * `extension`—, que son el perfil público: lo que sale en el directorio y junto
 * a cada publicación. Eso es «Perfil profesional», y se edita en el sitio.
 * El teléfono NO está ahí: es del maestro, y se mantiene desde Mi ficha.
 *
 * ## Lo que NO está aquí
 *
 * El aviso de datos pendientes es del **shell** (`app.js`), no de esta pantalla:
 * el gate de datos iniciales solo dispara en `preregistro`, así que una columna
 * que Bienestar agregue después no se le pide nunca a quien ya está activo — y
 * por eso tiene que verse se navegue a donde se navegue. Llegó a existir tres
 * veces (shell, pantalla y panel) y se dejó una el 2026-09-04. Lo que queda aquí
 * es la cuenta en el botón «Mi ficha»: el estado del control que abre el sitio
 * donde se arregla, no una cuarta copia del mensaje.
 *
 * ## Cómo se opera
 *
 *   · Extensión, sobre mí y habilidades se editan **en el sitio**: pulsar el
 *     dato lo convierte en campo. El modal antiguo abría tres para corregir uno.
 *   · Mi ficha es un panel lateral y no un modal: el formulario existe para
 *     completar lo que al perfil le falta, y taparlo mientras se rellena era lo
 *     contrario de lo que hacía falta.
 *   · Cada bloque carga por su cuenta. Antes tres peticiones en paralelo
 *     bloqueaban la pantalla entera: si los kudos tardaban, no se veía ni el
 *     nombre.
 */
export function Perfil() {
  const usuario = Auth.getUser();
  const perfil = usePerfil();
  const certificados = useCertificados();
  const kudos = useKudos(usuario?.id);
  const ficha = useMiFicha();
  const guardarPerfil = useGuardarPerfil();

  // `#perfil/ficha` abre el panel al montar: es por donde entra el botón
  // «Completar» del aviso del shell. Se lee UNA vez, en el estado inicial, y no
  // en un efecto: leerlo en cada render lo volvería a abrir después de que el
  // usuario lo cerrara, porque el hash sigue ahí.
  const [fichaAbierta, setFichaAbierta] = useState(() => window._routeParams?.[0] === 'ficha');
  const [claveAbierta, setClaveAbierta] = useState(false);

  const pendientes = ficha.data?.pendientes?.length ?? 0;

  if (perfil.isError) {
    return (
      <div className="react-root">
        <EstadoError err={perfil.error} que="tu perfil" onReintentar={() => void perfil.refetch()} />
      </div>
    );
  }

  const p = perfil.data;

  return (
    <div className="react-root flex flex-col gap-5 pb-10">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-8 font-bold tracking-tight text-brand-ink">Mi perfil</h1>
          <p className="mt-1 text-3 text-muted">
            Tu información en la organización, tu formación y tus certificados.
          </p>
        </div>
        <Button variant="secondary" onClick={() => setFichaAbierta(true)}>
          <i className="bi bi-person-vcard" aria-hidden="true" /> Mi ficha
          {pendientes > 0 && (
            <>
              {/* `inline-grid` y no `grid`: el contenido del botón vive dentro
                  de un <span> en línea, así que una caja de bloque aquí parte
                  el botón en dos renglones. Se veía en el navegador y no en el
                  código — el `whitespace-nowrap` del botón no salva de esto. */}
              <span
                aria-hidden="true"
                className="ml-1 inline-grid min-w-5 place-items-center rounded-full
                           bg-warn/25 px-1.5 text-1 font-bold text-warn-ink"
              >
                {pendientes}
              </span>
              {/* El número solo no dice de qué es. Quien no lo ve necesita la
                  frase entera, no un «2» suelto detrás de «Mi ficha». */}
              <span className="sr-only">
                {pendientes === 1 ? ', falta 1 dato' : `, faltan ${pendientes} datos`}
              </span>
            </>
          )}
        </Button>
      </header>

      <Identidad perfil={p} cargando={perfil.isLoading} />

      <ResumenFormacion
        cursos={p?.courses}
        certificados={certificados.data}
        cargando={perfil.isLoading}
      />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex flex-col gap-5">
          <InformacionLaboral perfil={p} ficha={ficha.data} cargando={ficha.isLoading} />
          <MisDatos ficha={ficha.data} cargando={ficha.isLoading} onAbrir={() => setFichaAbierta(true)} />
          <PerfilProfesional perfil={p} onGuardar={guardarPerfil.mutateAsync} />
        </div>

        <div className="flex flex-col gap-5">
          <Reconocimientos consulta={kudos} />
          <Consentimientos />
          <Card>
            <CardHeader titulo="Cuenta" />
            <CardBody>
              <Button variant="secondary" block onClick={() => setClaveAbierta(true)}>
                <i className="bi bi-key" aria-hidden="true" /> Cambiar contraseña
              </Button>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Formación: las dos listas a la par. En la barra de 340 px cada
          certificado ocupaba cuatro renglones —el botón encima de las fechas y
          «Emitido … · vence …» partido en tres— porque no cabe. */}
      <div className="grid gap-5 md:grid-cols-2">
        <MisCursos cursos={p?.courses} cargando={perfil.isLoading} />
        <MisCertificados consulta={certificados} />
      </div>

      <MiFicha abierta={fichaAbierta} onCerrar={() => setFichaAbierta(false)} />
      <CambiarContrasena abierto={claveAbierta} onCerrar={() => setClaveAbierta(false)} />
    </div>
  );
}

/* ── Identidad ─────────────────────────────────────────────────────────── */

/**
 * Quién soy. Es el bloque de mayor jerarquía de la página y no lleva ninguna
 * acción de edición: el único dato que se cambia desde aquí es la foto, y su
 * botón vive SOBRE la foto, que es donde se busca.
 */
function Identidad({ perfil: p, cargando }: { perfil?: ReturnType<typeof usePerfil>['data']; cargando: boolean }) {
  const antiguedad = antiguedadDesde(p?.hire_date);

  if (cargando) {
    return (
      <Card>
        <CardBody className="flex items-center gap-5">
          <Skeleton className="size-24 rounded-full" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-8 w-72" />
            <Skeleton className="h-4 w-56" />
            <Skeleton className="h-4 w-40" />
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody className="flex flex-wrap items-center gap-x-6 gap-y-4">
        <Foto url={p?.avatar_url} nombre={p?.full_name} />
        <div className="min-w-0 flex-1">
          <h2 className="text-8 font-bold leading-tight tracking-tight text-brand-ink">
            {p?.full_name}
          </h2>
          {p?.position && (
            <p className="mt-1 text-5 leading-snug text-ink">{p.position}</p>
          )}
          <p className="mt-1 text-3 text-muted">
            {[p?.area_name, p?.sede_name].filter(Boolean).join(' · ') || 'Sin área ni sede asignadas'}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Badge tone={p?.is_active === false ? 'neutral' : 'ok'}>
              {p?.is_active === false ? 'Inactivo' : 'Activo'}
            </Badge>
            {antiguedad && (
              <span className="text-2 text-muted">
                <i className="bi bi-hourglass-split mr-1" aria-hidden="true" />
                {antiguedad} en la organización
              </span>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function Foto({ url, nombre }: { url?: string | null; nombre?: string }) {
  const subir = useSubirFoto();
  const entrada = useRef<HTMLInputElement>(null);
  const [falloLaFoto, setFalloLaFoto] = useState(false);
  const [bg, fg] = Utils.avatarColor(nombre ?? '');

  return (
    <div className="relative shrink-0">
      <div
        className="grid size-24 place-items-center overflow-hidden rounded-full text-8 font-bold"
        style={url && !falloLaFoto ? undefined : { background: bg, color: fg }}
      >
        {url && !falloLaFoto ? (
          // La URL del avatar es PRESIGNADA y caduca (MINIO_PRESIGN_EXPIRY). Si
          // expira a media sesión el <img> no carga y, sin respaldo, aquí queda
          // un círculo vacío sin ningún error a la vista. El shell ya resolvía
          // esto con un `onerror` que vuelve a las iniciales (setupSidebar en
          // app.js); esta pantalla no lo tenía.
          <img
            src={url}
            alt=""
            className="size-full object-cover"
            onError={() => setFalloLaFoto(true)}
          />
        ) : (
          Utils.avatarInitials(nombre ?? '')
        )}
      </div>
      <input
        ref={entrada}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="sr-only"
        onChange={async (e) => {
          const archivo = e.target.files?.[0];
          e.target.value = '';
          if (!archivo) return;
          // 5 MB, el mismo límite que ponía el Uploader vanilla.
          if (archivo.size > 5 * 1024 * 1024) {
            Utils.toast('La imagen no puede pesar más de 5 MB', 'error');
            return;
          }
          try {
            await subir.mutateAsync(archivo);
            Utils.toast('Foto actualizada', 'success');
          } catch (err) {
            Utils.toast(err instanceof Error ? err.message : 'No se pudo subir la foto', 'error');
          }
        }}
      />
      {/* El botón vive SOBRE la foto, que es donde se busca cambiarla, y no en
          una lista de acciones al pie. */}
      <button
        type="button"
        onClick={() => entrada.current?.click()}
        disabled={subir.isPending}
        aria-label="Cambiar foto de perfil"
        className="absolute -bottom-1 -right-1 grid size-9 place-items-center rounded-full
                   border-2 border-surface bg-brand text-on-brand
                   transition-colors hover:bg-brand-strong
                   focus-visible:focus-anillo
                   disabled:opacity-100
                   pointer-coarse:size-11"
      >
        <i className={cn('bi', subir.isPending ? 'bi-hourglass' : 'bi-camera-fill', 'text-2')}
           aria-hidden="true" />
      </button>
    </div>
  );
}

/* ── Resumen de formación ──────────────────────────────────────────────── */

/**
 * Tres cifras y una barra. Sale de lo que las consultas de la pantalla YA
 * traen: no hay endpoint de estadísticas y no se añade ninguno.
 *
 * Desaparece entero cuando no hay ningún curso, en vez de enseñar tres ceros:
 * un contador en cero enseña a ignorar el sitio donde algún día habrá un uno.
 */
function ResumenFormacion({
  cursos, certificados, cargando,
}: { cursos?: CursoDelPerfil[]; certificados?: Certificado[]; cargando: boolean }) {
  const r = resumenFormacion(cursos, certificados);
  if (cargando) return <Skeleton className="h-24 w-full rounded-md" />;
  if (!r.inscritos && !r.certificados) return null;

  return (
    <Card>
      <CardBody className="flex flex-wrap items-center gap-x-8 gap-y-5">
        {/* `min-w-60` para que las cifras y el progreso se separen en dos filas
            ANTES de estrecharse. Sin él, a 375 px el progreso se quedaba con sus
            224 px y las tres columnas repartían lo que sobraba: «Cursos
            inscritos» se recortaba a «C». Un ancho mínimo obliga al salto de
            línea, que es lo que se quería. */}
        <dl className="grid min-w-60 flex-1 grid-cols-3 gap-x-4 gap-y-1">
          <Cifra valor={r.inscritos} etiqueta="Cursos inscritos" />
          <Cifra valor={r.completados} etiqueta="Completados" />
          <Cifra valor={r.certificados} etiqueta="Certificados" />
        </dl>
        {r.progreso != null && (
          <div className="min-w-56 flex-1">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-2 font-semibold uppercase tracking-wide text-muted">
                Progreso general
              </span>
              <span className="text-4 font-bold tabular-nums text-ink">{r.progreso}%</span>
            </div>
            <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-sunken">
              <div
                className={cn('h-full rounded-full', r.progreso === 100 ? 'bg-ok' : 'bg-brand')}
                style={{ width: `${r.progreso}%` }}
              />
            </div>
            <p className="mt-1 text-1 text-muted">
              Promedio de avance de tus {r.inscritos === 1 ? 'curso' : `${r.inscritos} cursos`}.
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function Cifra({ valor, etiqueta }: { valor: number; etiqueta: string }) {
  return (
    <div className="min-w-0">
      <dd className="text-8 font-bold leading-none tabular-nums text-brand-ink">{valor}</dd>
      {/* Sin `truncate`: una etiqueta de dos palabras cabe en dos renglones, y
          recortarla la deja en una inicial que no dice nada. */}
      <dt className="mt-1 text-2 leading-snug text-muted">{etiqueta}</dt>
    </div>
  );
}

/* ── Información laboral (oficial) ─────────────────────────────────────── */

/** Las columnas laborales, en el orden en que se leen. La ETIQUETA no está
 *  aquí: se toma de `campos[].label` del servidor, así que renombrar una
 *  columna en Recursos y Datos la renombra también en esta pantalla. */
const CLAVES_LABORALES = [
  'empresa', 'sede', 'area', 'cargo', 'jefe_inmediato', 'tipo_contrato', 'fecha_ingreso', 'email',
] as const;

/**
 * Lo que la organización tiene registrado. Solo lectura, y se dice por qué.
 *
 * Estas columnas no son de solo lectura por decisión de esta pantalla: están en
 * `CAMPOS_BLOQUEADOS` (employee_db_service.py) y el motivo está escrito allí —
 * empresa, área y jefe deciden QUIÉN APRUEBA las solicitudes de esta persona;
 * si se las declarara ella misma, elegiría a su propio aprobador. El correo es
 * la credencial de acceso y la fecha de ingreso determina antigüedad y
 * aniversarios.
 *
 * Por eso el bloque no ofrece una edición ficticia: ofrece a quién pedirla.
 */
function InformacionLaboral({
  perfil: p, ficha, cargando,
}: { perfil?: ReturnType<typeof usePerfil>['data']; ficha?: Ficha; cargando: boolean }) {
  // El valor preferente es el del perfil, que ya trae los nombres resueltos;
  // la ficha completa el resto y aporta la etiqueta y el motivo.
  const desdeElPerfil: Record<string, string | null | undefined> = {
    area: p?.area_name,
    cargo: p?.position,
    sede: p?.sede_name,
    jefe_inmediato: p?.manager_name,
    email: p?.email,
    fecha_ingreso: fechaLarga(p?.hire_date),
  };

  const filas = CLAVES_LABORALES
    .map((clave) => ({ clave, g: gestionDe(ficha, clave) }))
    .filter(({ g }) => g.existe)
    .map(({ clave, g }) => ({
      clave,
      etiqueta: g.label ?? clave,
      valor: desdeElPerfil[clave] ?? valorDeFicha(g),
      gestion: g,
    }));

  return (
    <Card>
      <CardHeader
        titulo="Información laboral"
        meta="Datos oficiales de tu vinculación"
      />
      <CardBody className="flex flex-col gap-4">
        {cargando ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : (
          <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
            {filas.map((f) => (
              <div key={f.clave} className="min-w-0">
                <dt className="text-2 uppercase tracking-wide text-muted">{f.etiqueta}</dt>
                <dd className={cn('mt-0.5 break-words text-3',
                                  f.valor ? 'font-semibold text-ink' : 'text-muted')}>
                  {f.valor ?? 'Sin registrar'}
                </dd>
              </div>
            ))}
          </dl>
        )}

        {/* La nota va una vez al pie y no un candado por fila: son ocho datos y
            todos se administran igual, así que repetir el icono ocho veces es
            ruido que no distingue nada. */}
        <p className="flex items-start gap-2 border-t border-line pt-3 text-2 leading-relaxed text-muted">
          <i className="bi bi-lock mt-0.5 shrink-0" aria-hidden="true" />
          <span>
            Los administra Gestión Humana y no se editan desde el portal.
            <strong className="font-semibold text-ink-2"> ¿Encontraste un dato incorrecto?</strong>{' '}
            Comunícate con Bienestar para solicitar su actualización.
          </span>
        </p>
      </CardBody>
    </Card>
  );
}

function valorDeFicha(g: Gestion): string | undefined {
  if (!g.valor) return undefined;
  // Una columna DATE llega como `yyyy-mm-dd`. `fmtDateOnly` la construye con los
  // componentes locales; `fmtDate` la leería como medianoche UTC y en Colombia
  // imprimiría el día anterior.
  return /^\d{4}-\d{2}-\d{2}$/.test(g.valor) ? Utils.fmtDateOnly(g.valor) : g.valor;
}

/* ── Mis datos (los que mantiene el colaborador) ───────────────────────── */

/**
 * Lo que el colaborador SÍ puede mantener al día.
 *
 * La lista se calcula, no se enumera: son las columnas que el servidor devuelve
 * con `editable: true`. Si Bienestar abre mañana una columna nueva a la
 * autogestión, aparece aquí sola; si cierra una, desaparece. Una lista escrita
 * a mano en el cliente dejaría de coincidir con lo que el endpoint acepta en
 * cuanto alguien tocara la marca — y el colaborador vería un campo que al
 * guardarse se descarta en silencio.
 *
 * Los que están vacíos se marcan, porque son exactamente los que el aviso del
 * shell está contando.
 */
function MisDatos({
  ficha, cargando, onAbrir,
}: { ficha?: Ficha; cargando: boolean; onAbrir: () => void }) {
  const campos = camposQueMantieneElColaborador(ficha);
  const sensiblesBloqueados = (ficha?.campos ?? []).filter((c) => !c.editable && c.sensible);

  return (
    <Card>
      <CardHeader
        titulo="Mis datos"
        meta="Los mantienes tú"
        accion={
          campos.length > 0 ? (
            <Button size="sm" variant="secondary" onClick={onAbrir}>
              <i className="bi bi-pencil" aria-hidden="true" /> Actualizar
            </Button>
          ) : undefined
        }
      />
      <CardBody className="flex flex-col gap-4">
        {cargando ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : campos.length === 0 ? (
          <p className="text-3 text-muted">
            Ahora mismo no hay ningún dato tuyo que puedas actualizar por tu cuenta.
            Todos los administra Gestión Humana.
          </p>
        ) : (
          <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
            {campos.map((c, i) => (
              <div key={(ficha?.campos ?? [])[i]?.key ?? c.label} className="min-w-0">
                <dt className="text-2 uppercase tracking-wide text-muted">{c.label}</dt>
                <dd className="mt-0.5 flex flex-wrap items-center gap-2 break-words text-3">
                  {c.valor ? (
                    <span className="font-semibold text-ink">{valorDeFicha(c)}</span>
                  ) : (
                    <>
                      <span className="text-muted">Sin completar</span>
                      <Badge tone="warn">Pendiente</Badge>
                    </>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        )}

        {/* Un dato sensible bloqueado NO es un callejón sin salida: lo abre el
            propio colaborador autorizando el tratamiento, y esa tarjeta está en
            esta misma pantalla. Decirlo es la diferencia entre «no puedes» y
            «puedes, y así». */}
        {sensiblesBloqueados.length > 0 && (
          <p className="flex items-start gap-2 border-t border-line pt-3 text-2 leading-relaxed text-muted">
            <i className="bi bi-shield-exclamation mt-0.5 shrink-0" aria-hidden="true" />
            <span>
              {sensiblesBloqueados.length === 1
                ? `«${sensiblesBloqueados[0]!.label}» es un dato sensible y está cerrado.`
                : `Hay ${sensiblesBloqueados.length} datos sensibles cerrados.`}{' '}
              Se abren autorizando el tratamiento de datos sensibles en
              <strong className="font-semibold text-ink-2"> Mis consentimientos</strong>.
            </span>
          </p>
        )}
      </CardBody>
    </Card>
  );
}

/* ── Perfil profesional (el perfil público) ────────────────────────────── */

/**
 * Los tres campos que acepta `PUT /users/me/profile`: `extension`, `bio` y
 * `skills`. Ni uno más — el modelo `ProfileUpdate` descarta lo demás.
 *
 * Se llamaba «Tuyo para editar», y el nombre no decía lo importante: que esto
 * no es información privada que uno mantiene para sí, sino **lo que ve el resto
 * de la organización** en el directorio y junto a cada publicación suya. De ahí
 * el nombre y la frase de arriba.
 */
function PerfilProfesional({
  perfil: p, onGuardar,
}: {
  perfil?: ReturnType<typeof usePerfil>['data'];
  onGuardar: (cambios: { extension?: string; bio?: string; skills?: string }) => Promise<unknown>;
}) {
  const habilidades = (p?.skills ?? '').split(',').map((s) => s.trim()).filter(Boolean);

  return (
    <Card>
      <CardHeader
        titulo="Perfil profesional"
        meta="Lo que ve tu equipo en el directorio y en cada publicación tuya"
      />
      <CardBody className="flex flex-col gap-1">
        <p className="mb-2 flex items-start gap-2 text-2 leading-relaxed text-muted">
          <i className="bi bi-pencil-square mt-0.5 shrink-0" aria-hidden="true" />
          <span>Estos tres los editas tú: pulsa el dato para cambiarlo.</span>
        </p>
        <CampoEnLinea
          etiqueta="Extensión"
          icono="bi-telephone-inbound"
          valor={p?.extension ?? ''}
          placeholder="Añadir extensión"
          ayuda="Tu extensión interna, si tienes una."
          onGuardar={(v) => onGuardar({ extension: v })}
        />
        <CampoEnLinea
          etiqueta="Sobre mí"
          icono="bi-chat-quote"
          valor={p?.bio ?? ''}
          placeholder="Añadir una breve descripción profesional"
          multilinea
          onGuardar={(v) => onGuardar({ bio: v })}
        />
        <CampoEnLinea
          etiqueta="Habilidades"
          icono="bi-stars"
          valor={p?.skills ?? ''}
          placeholder="Añadir habilidades"
          ayuda="Sepáralas con comas. Aparecen en el directorio y son buscables."
          onGuardar={(v) => onGuardar({ skills: v })}
        />
        {habilidades.length > 0 && (
          <div className="ml-7 mt-1.5 flex flex-wrap gap-1.5">
            {habilidades.map((s) => <Badge key={s} tone="neutral">{s}</Badge>)}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/* ── Formación ─────────────────────────────────────────────────────────── */

function MisCursos({ cursos, cargando }: { cursos?: CursoDelPerfil[]; cargando: boolean }) {
  return (
    <Card>
      <CardHeader titulo="Mis cursos" meta={cursos?.length ? `${cursos.length} inscritos` : undefined} />
      {cargando ? (
        <CardBody className="flex flex-col gap-3">
          {[0, 1].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
        </CardBody>
      ) : cursos?.length ? (
        <ul>
          {cursos.map((c) => <FilaCurso key={c.course_id} curso={c} />)}
        </ul>
      ) : (
        <EstadoVacio
          compacto
          icono="bi-mortarboard"
          titulo="Todavía no tienes cursos"
          descripcion="Bienestar te asignará los obligatorios. Mientras tanto, puedes explorar el catálogo."
          accion={{ texto: 'Ver cursos', onClick: () => window.APP?.navigate('courses') }}
        />
      )}
    </Card>
  );
}

/**
 * Una fila de curso: qué es, en qué estado está, cuánto llevas y qué puedes
 * hacer. El estado va en palabra y en color —nunca solo en color—, y la barra
 * lleva su cifra al lado en vez de obligar a estimarla a ojo.
 */
function FilaCurso({ curso }: { curso: CursoDelPerfil }) {
  const completo = Boolean(curso.completed_at);
  const pct = Math.round(curso.progress_pct ?? 0);
  const empezado = pct > 0;

  return (
    <li className="flex flex-col gap-2.5 border-b border-line px-4 py-3.5 last:border-0">
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 flex-1 text-3 font-semibold leading-snug text-ink">
          {curso.course_title ?? `Curso #${curso.course_id}`}
        </p>
        <Badge tone={completo ? 'ok' : empezado ? 'info' : 'neutral'}>
          {completo ? 'Completado' : empezado ? 'En curso' : 'Sin empezar'}
        </Badge>
      </div>

      <div className="flex items-center gap-3">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-sunken">
          <div
            className={cn('h-full rounded-full', completo ? 'bg-ok' : 'bg-brand')}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="shrink-0 text-2 font-semibold tabular-nums text-ink-2">{pct}%</span>
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="min-w-0 flex-1 text-2 leading-relaxed text-muted">
          {completo
            ? `Completado el ${Utils.fmtDate(curso.completed_at!)}${curso.quiz_score ? ` · calificación ${Math.round(curso.quiz_score)}%` : ''}`
            : empezado ? 'Puedes retomarlo donde lo dejaste.' : 'Aún no lo has abierto.'}
        </p>
        <Button
          size="sm"
          className="shrink-0"
          variant={completo ? 'secondary' : 'primary'}
          onClick={() => window.APP?.navigate(`course-detail/${curso.course_id}`)}
        >
          {completo ? 'Repasar' : empezado ? 'Continuar' : 'Empezar'}
        </Button>
      </div>
    </li>
  );
}

function MisCertificados({ consulta }: { consulta: ReturnType<typeof useCertificados> }) {
  return (
    <Card>
      <CardHeader
        titulo="Mis certificados"
        meta={consulta.data?.length ? `${consulta.data.length} emitidos` : undefined}
      />
      {consulta.isLoading ? (
        <CardBody className="flex flex-col gap-3">
          {[0, 1].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
        </CardBody>
      ) : consulta.isError ? (
        <EstadoError err={consulta.error} que="tus certificados"
                     onReintentar={() => void consulta.refetch()} />
      ) : consulta.data?.length ? (
        <ul>
          {consulta.data.map((c) => <FilaCertificado key={c.course_id} cert={c} />)}
        </ul>
      ) : (
        <EstadoVacio
          compacto
          icono="bi-patch-check"
          titulo="Sin certificados aún"
          descripcion="Al terminar un curso con evaluación, su certificado aparece aquí."
        />
      )}
    </Card>
  );
}

function FilaCertificado({ cert }: { cert: Certificado }) {
  const tono = cert.status === 'vencido' ? 'danger' : cert.status === 'por_vencer' ? 'warn' : 'ok';
  const etiqueta = cert.status === 'vencido' ? 'Vencido'
    : cert.status === 'por_vencer' ? 'Por vencer' : 'Vigente';

  // `Utils.fmtDate` y no `slice(0, 10)`, y tampoco `fmtDateOnly`.
  //
  // `expires_at` es DateTime(timezone=True) —no una columna DATE— y vale
  // `completed_at + N meses`, así que arrastra la hora a la que la persona
  // terminó el curso. Cortar los diez primeros caracteres da el día en **UTC**:
  // quien termine un curso después de las 19:00 en Colombia ve el día
  // siguiente. Y el PDF imprime el día LOCAL —para eso existe
  // `certificate_service._fecha_local()`—, así que la pantalla y el papel
  // decían fechas distintas para la misma vigencia.
  //
  // `fmtDateOnly` es para columnas DATE sueltas; sobre una marca de tiempo cae
  // igualmente en `fmtDate`, pero usarla aquí diría que esto es un DATE y no lo
  // es.
  const vence = cert.expires_at
    ? `${cert.status === 'vencido' ? 'Venció el' : 'Vence el'} ${Utils.fmtDate(cert.expires_at)}`
    : 'Sin fecha de vencimiento';

  return (
    <li className="flex flex-col gap-2.5 border-b border-line px-4 py-3.5 last:border-0">
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 flex-1 text-3 font-semibold leading-snug text-ink">
          <i className="bi bi-patch-check-fill mr-1.5 text-muted" aria-hidden="true" />
          {cert.course_title ?? `Curso #${cert.course_id}`}
        </p>
        <Badge tone={tono}>{etiqueta}</Badge>
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="min-w-0 flex-1 text-2 leading-relaxed text-muted">
          Emitido el {Utils.fmtDate(cert.issued_at)}
          <span className="mx-1.5" aria-hidden="true">·</span>
          {vence}
          {cert.renewal_count ? ` · renovado ${cert.renewal_count}×` : ''}
        </p>
        {cert.status === 'vencido' ? (
          <Button size="sm" className="shrink-0"
                  onClick={() => window.APP?.navigate(`course-detail/${cert.course_id}`)}>
            <i className="bi bi-arrow-repeat" aria-hidden="true" /> Recertificar
          </Button>
        ) : cert.download_url ? (
          <a
            href={cert.download_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-8 shrink-0 items-center gap-2 rounded-sm border-2 border-brand-ink
                       px-3 text-2 font-semibold text-brand-ink transition-colors
                       hover:bg-brand hover:text-on-brand
                       focus-visible:focus-anillo
                       pointer-coarse:min-h-11"
          >
            <i className="bi bi-download" aria-hidden="true" /> Descargar
          </a>
        ) : null}
      </div>
    </li>
  );
}

/* ── Barra lateral ─────────────────────────────────────────────────────── */

function Reconocimientos({ consulta }: { consulta: ReturnType<typeof useKudos> }) {
  return (
    <Card>
      <CardHeader
        titulo="Mis reconocimientos"
        meta={consulta.data?.length ? `${consulta.data.length} recibidos` : undefined}
      />
      {consulta.isLoading ? (
        <CardBody><Skeleton className="h-16 w-full" /></CardBody>
      ) : consulta.data?.length ? (
        <ul className="max-h-56 overflow-y-auto">
          {consulta.data.slice(0, 5).map((k, i) => (
            <li key={k.id ?? i} className="border-b border-line px-4 py-3 last:border-0">
              <p className="text-3 font-semibold text-ink">{k.badge}</p>
              {k.message && (
                <p className="mt-0.5 text-2 italic leading-relaxed text-muted">
                  «{k.message}»
                </p>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <EstadoVacio
          compacto
          icono="bi-award"
          titulo="Sin reconocimientos aún"
          descripcion="Cuando alguien te reconozca, aparecerá aquí."
        />
      )}
    </Card>
  );
}

/**
 * Consentimientos (Habeas Data).
 *
 * Esta tarjeta es el ÚNICO sitio donde alguien puede cambiar de opinión sobre
 * el tratamiento de sus datos: `/legal/pending` descarta todo documento que ya
 * tenga fila, sin mirar si fue un sí o un no, así que quien rechazó una vez no
 * lo vuelve a ver nunca. Sin esta pantalla se quedaría sin salida, y con
 * columnas sensibles del maestro eso significa un campo que no le aparece en
 * Mi ficha para siempre y sin explicación.
 *
 * Los obligatorios se muestran pero no se ofrecen: el acceso al portal depende
 * de ellos y el servidor responde 400 si se intenta revocarlos.
 */
function Consentimientos() {
  const { data, isLoading, isError, error, refetch } = useConsentimientos();
  const cambiar = useCambiarConsentimiento();
  const [confirmando, setConfirmando] = useState<{ c: Consentimiento; autorizar: boolean } | null>(null);

  return (
    <Card>
      <CardHeader titulo="Mis consentimientos" meta="Tratamiento de datos" />
      {isLoading ? (
        <CardBody><Skeleton className="h-16 w-full" /></CardBody>
      ) : isError ? (
        <EstadoError err={error} que="tus consentimientos" onReintentar={() => void refetch()} />
      ) : !data?.length ? (
        <EstadoVacio compacto icono="bi-shield-check" titulo="No hay documentos publicados" />
      ) : (
        <ul>
          {data.map((c) => {
            const estado = !c.decidido
              ? { txt: 'Sin responder', tono: 'neutral' as const }
              : c.accepted
                ? { txt: 'Autorizado', tono: 'ok' as const }
                : { txt: 'No autorizado', tono: 'neutral' as const };
            return (
              <li key={c.doc_key} className="flex items-start gap-3 border-b border-line px-4 py-3 last:border-0">
                <div className="min-w-0 flex-1">
                  <p className="text-3 font-semibold text-ink">
                    {c.title ?? c.doc_key}
                    {c.version && <span className="ml-1 text-1 font-normal text-muted">v{c.version}</span>}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <Badge tone={estado.tono}>{estado.txt}</Badge>
                    {c.decidido && c.accepted_at && (
                      <span className="text-1 text-muted">{Utils.fmtDatetime(c.accepted_at)}</span>
                    )}
                    {c.revocable && <span className="text-1 text-muted">voluntario</span>}
                  </div>
                </div>
                {c.revocable && (
                  <Button
                    size="sm"
                    variant={c.accepted ? 'ghost' : 'secondary'}
                    onClick={() => setConfirmando({ c, autorizar: !c.accepted })}
                  >
                    {c.accepted ? 'Revocar' : 'Autorizar'}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {confirmando && (
        <ConfirmarConsentimiento
          estado={confirmando}
          cargando={cambiar.isPending}
          onCerrar={() => setConfirmando(null)}
          onConfirmar={async () => {
            try {
              await cambiar.mutateAsync({
                docKey: confirmando.c.doc_key,
                autorizar: confirmando.autorizar,
              });
              Utils.toast(
                confirmando.autorizar ? 'Autorización registrada' : 'Autorización revocada',
                'success',
              );
              setConfirmando(null);
            } catch (e) {
              Utils.toast(e instanceof Error ? e.message : 'No se pudo registrar', 'error');
            }
          }}
        />
      )}
    </Card>
  );
}

function ConfirmarConsentimiento({
  estado, cargando, onCerrar, onConfirmar,
}: {
  estado: { c: Consentimiento; autorizar: boolean };
  cargando: boolean;
  onCerrar: () => void;
  onConfirmar: () => void;
}) {
  // Revocar NO borra lo ya entregado, y decir lo contrario sería mentir: corta
  // hacia adelante y deja constancia para que Bienestar decida sobre el
  // histórico. El texto es literal del original, que lo dice bien.
  const descripcion = estado.autorizar
    ? 'Podrás revocarla cuando quieras desde esta misma pantalla.'
    : 'Dejarás de ver esos campos en Mi ficha y el sistema no volverá a pedírtelos. '
      + 'Lo que ya entregaste se conserva hasta que Gestión Humana lo suprima; '
      + 'queda constancia de tu solicitud.';

  return (
    <ConfirmDialog
      abierto
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo={estado.autorizar ? 'Autorizar el tratamiento' : 'Revocar la autorización'}
      descripcion={descripcion}
      textoConfirmar={estado.autorizar ? 'Autorizar' : 'Revocar'}
      destructivo={!estado.autorizar}
      cargando={cargando}
      onConfirmar={onConfirmar}
    />
  );
}
